import { CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined, SafetyCertificateOutlined, WarningOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, Modal, Space, Spin, Table, Tag, Tooltip } from "antd";
import PropTypes from "prop-types";
import { Component } from "react";

const CONFIDENCE_COLORS = {
    high: "green",
    medium: "orange",
    low: "red",
};

const CONFIDENCE_ICONS = {
    high: <CheckCircleOutlined />,
    medium: <ExclamationCircleOutlined />,
    low: <WarningOutlined />,
};

const PII_CATEGORY_COLORS = {
    "Direct Identifier": "red",
    "Quasi-Identifier": "orange",
    "Financial PII": "purple",
    "Health PII": "magenta",
    "Digital Identifier": "blue",
    "Location Data": "cyan",
};

class PIISuggestionsModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedFieldIds: new Set(),
            selectAll: false,
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.visible && !this.props.visible) {
            this.setState({ selectedFieldIds: new Set(), selectAll: false });
        }
        if (!prevProps.piiSuggestionsData && this.props.piiSuggestionsData) {
            const changedFieldIds = new Set();
            const fields = this.props.piiSuggestionsData?.fields || [];
            fields.forEach((field) => {
                if (this.hasChanges(field)) {
                    changedFieldIds.add(field.fieldId);
                }
            });
            this.setState({
                selectedFieldIds: changedFieldIds,
                selectAll: changedFieldIds.size === fields.length && fields.length > 0,
            });
        }
    }

    hasChanges = (field) => {
        const isPIIChanged = field.isPII !== field.suggestedIsPII;
        const currentTagIds = new Set((field.classifiers || []).map((c) => c.tagId));
        const newSuggestedTags = (field.suggestedClassifiers || []).filter((s) => !currentTagIds.has(s.tagId));
        return isPIIChanged || newSuggestedTags.length > 0;
    };

    isAiAvailable = () => {
        const fields = this.props.piiSuggestionsData?.fields || [];
        return fields.some((f) => f.confidence !== null);
    };

    onSelectAll = (e) => {
        const checked = e.target.checked;
        const fields = this.props.piiSuggestionsData?.fields || [];
        if (checked) {
            const allIds = new Set(fields.map((f) => f.fieldId));
            this.setState({ selectedFieldIds: allIds, selectAll: true });
        } else {
            this.setState({ selectedFieldIds: new Set(), selectAll: false });
        }
    };

    onSelectField = (fieldId, checked) => {
        this.setState((prevState) => {
            const newSet = new Set(prevState.selectedFieldIds);
            if (checked) {
                newSet.add(fieldId);
            } else {
                newSet.delete(fieldId);
            }
            const fields = this.props.piiSuggestionsData?.fields || [];
            return {
                selectedFieldIds: newSet,
                selectAll: newSet.size === fields.length && fields.length > 0,
            };
        });
    };

    getSelectedCount = () => {
        return this.state.selectedFieldIds.size;
    };

    getChangedCount = () => {
        const fields = this.props.piiSuggestionsData?.fields || [];
        return fields.filter((f) => this.hasChanges(f)).length;
    };

    handleSave = () => {
        const { piiSuggestionsData } = this.props;
        const { selectedFieldIds } = this.state;

        if (selectedFieldIds.size === 0) return;

        const fields = piiSuggestionsData?.fields || [];
        const updatedFields = fields
            .filter((field) => selectedFieldIds.has(field.fieldId))
            .map((field) => {
                const currentTagIds = new Set((field.classifiers || []).map((c) => c.tagId));
                const newTags = (field.suggestedClassifiers || []).filter((s) => !currentTagIds.has(s.tagId));
                const mergedClassifiers = [...(field.classifiers || []), ...newTags];

                return {
                    fieldId: field.fieldId,
                    name: field.name,
                    description: field.description,
                    active: field.active,
                    isPrimaryKey: field.isPrimaryKey,
                    isSortKey: field.isSortKey,
                    dataType: field.dataType,
                    max: field.max,
                    min: field.min,
                    maxWidth: field.maxWidth,
                    maxHeight: field.maxHeight,
                    maxSize: field.maxSize,
                    isRequired: field.isRequired,
                    isMultivalue: field.isMultivalue,
                    isPII: field.suggestedIsPII,
                    classifiers: mergedClassifiers,
                    allowedBooleanValues: field.allowedBooleanValues,
                    tenantId: field.tenantId,
                    businessAreaId: field.businessAreaId,
                    schemaId: field.schemaId,
                    createdBy: field.createdBy,
                    createdByEmail: field.createdByEmail,
                    updatedBy: field.updatedBy,
                    updatedByEmail: field.updatedByEmail,
                    createdDate: field.createdDate,
                    updatedDate: field.updatedDate,
                    deleted: field.deleted,
                    version: field.version,
                };
            });

        if (updatedFields.length > 0) {
            this.props.onSave(piiSuggestionsData.businessAreaId, piiSuggestionsData.schemaId, updatedFields);
        }
    };

    getColumns = () => {
        const fields = this.props.piiSuggestionsData?.fields || [];
        return [
            {
                title: (
                    <Checkbox
                        checked={this.state.selectAll}
                        indeterminate={this.state.selectedFieldIds.size > 0 && this.state.selectedFieldIds.size < fields.length}
                        onChange={this.onSelectAll}
                    />
                ),
                dataIndex: "fieldId",
                key: "select",
                width: 50,
                render: (fieldId) => <Checkbox checked={this.state.selectedFieldIds.has(fieldId)} onChange={(e) => this.onSelectField(fieldId, e.target.checked)} />,
            },
            {
                title: "Field Name",
                dataIndex: "name",
                key: "name",
                width: 160,
                render: (name, record) => <span className="font-medium">{name}</span>,
            },
            {
                title: "Data Type",
                dataIndex: "dataType",
                key: "dataType",
                width: 100,
                render: (dataType) => <Tag>{dataType}</Tag>,
            },
            {
                title: "Current PII",
                dataIndex: "isPII",
                key: "isPII",
                width: 100,
                align: "center",
                render: (isPII) => <Tag color={isPII ? "red" : "default"}>{isPII ? "Yes" : "No"}</Tag>,
            },
            {
                title: "Suggested PII",
                dataIndex: "suggestedIsPII",
                key: "suggestedIsPII",
                width: 120,
                align: "center",
                render: (suggestedIsPII, record) => {
                    const hasChange = record.isPII !== suggestedIsPII;
                    return (
                        <Tag color={suggestedIsPII ? "red" : "default"} className={hasChange ? "ring-2 ring-blue-300" : ""}>
                            {suggestedIsPII ? "Yes" : "No"}
                            {hasChange && <span className="ml-1 text-blue-500">*</span>}
                        </Tag>
                    );
                },
            },
            {
                title: "PII Category",
                dataIndex: "piiCategory",
                key: "piiCategory",
                width: 150,
                render: (piiCategory) => (piiCategory ? <Tag color={PII_CATEGORY_COLORS[piiCategory] || "default"}>{piiCategory}</Tag> : <span className="text-gray-400">—</span>),
            },
            {
                title: "Confidence",
                dataIndex: "confidence",
                key: "confidence",
                width: 110,
                align: "center",
                render: (confidence) =>
                    confidence ? (
                        <Tag icon={CONFIDENCE_ICONS[confidence]} color={CONFIDENCE_COLORS[confidence]}>
                            {confidence.charAt(0).toUpperCase() + confidence.slice(1)}
                        </Tag>
                    ) : (
                        <span className="text-gray-400">—</span>
                    ),
            },
            {
                title: "Reason",
                dataIndex: "reason",
                key: "reason",
                width: 80,
                align: "center",
                render: (reason) =>
                    reason ? (
                        <Tooltip title={reason} placement="topLeft" overlayStyle={{ maxWidth: 400 }}>
                            <InfoCircleOutlined className="cursor-pointer text-blue-500" />
                        </Tooltip>
                    ) : (
                        <span className="text-gray-400">—</span>
                    ),
            },
            {
                title: "Suggested Tags",
                dataIndex: "suggestedClassifiers",
                key: "suggestedClassifiers",
                width: 200,
                render: (suggestedClassifiers, record) => {
                    if (!suggestedClassifiers || suggestedClassifiers.length === 0) {
                        return <span className="text-gray-400">—</span>;
                    }
                    const currentTagIds = new Set((record.classifiers || []).map((c) => c.tagId));
                    return (
                        <div className="flex flex-wrap gap-1">
                            {suggestedClassifiers.map((tag) => {
                                const isNew = !currentTagIds.has(tag.tagId);
                                return (
                                    <Tag key={tag.tagId || tag.tagName} color={isNew ? "blue" : "default"} className={isNew ? "ring-1 ring-blue-300" : ""}>
                                        {tag.tagName}
                                        {isNew && <span className="ml-1 text-xs">new</span>}
                                    </Tag>
                                );
                            })}
                        </div>
                    );
                },
            },
        ];
    };

    render() {
        const { visible, loading, piiSuggestionsData, savingPii, onCancel, onRegenerate } = this.props;
        const fields = piiSuggestionsData?.fields || [];
        const aiAvailable = this.isAiAvailable();
        const changedCount = this.getChangedCount();
        const selectedCount = this.getSelectedCount();

        return (
            <Modal
                title={
                    <div className="flex items-center gap-2">
                        <SafetyCertificateOutlined className="text-lg text-purple-600" />
                        <span className="font-semibold">AI PII Suggestions</span>
                        {piiSuggestionsData?.schemaName && <span className="text-sm font-normal text-gray-500">— {piiSuggestionsData.schemaName}</span>}
                    </div>
                }
                open={visible}
                onCancel={onCancel}
                width={1200}
                centered
                destroyOnClose
                footer={
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                            {selectedCount > 0 ? (
                                <span>
                                    {selectedCount} field{selectedCount !== 1 ? "s" : ""} selected
                                    {changedCount > 0 && <span className="ml-2 text-blue-500">({changedCount} with AI changes)</span>}
                                </span>
                            ) : (
                                "Select fields to apply PII suggestions to form"
                            )}
                        </div>
                        <Space>
                            <Button onClick={onCancel}>Cancel</Button>
                            <Button onClick={onRegenerate} disabled={loading || savingPii}>
                                Regenerate
                            </Button>
                            <Button type="primary" onClick={this.handleSave} disabled={selectedCount === 0 || savingPii} loading={savingPii}>
                                Apply to Form
                            </Button>
                        </Space>
                    </div>
                }
                styles={{
                    body: {
                        maxHeight: "65vh",
                        overflow: "auto",
                        padding: "16px 0",
                    },
                }}
            >
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Spin size="large" />
                        <p className="mt-4 text-base text-gray-600">Analyzing schema fields for PII with AI...</p>
                        <p className="text-sm text-gray-400">This may take up to 30 seconds.</p>
                    </div>
                ) : (
                    <>
                        {!aiAvailable && fields.length > 0 && (
                            <Alert
                                message="AI Suggestions Unavailable"
                                description="AI suggestions are temporarily unavailable. Showing current field values. Please try again later."
                                type="warning"
                                showIcon
                                className="mx-4 mb-4"
                            />
                        )}
                        {changedCount > 0 && aiAvailable && (
                            <Alert
                                message={
                                    <span>
                                        AI found <strong>{changedCount}</strong> field{changedCount !== 1 ? "s" : ""} with suggested PII changes. Fields with differences are
                                        highlighted and pre-selected.
                                    </span>
                                }
                                type="info"
                                showIcon
                                className="mx-4 mb-4"
                            />
                        )}
                        {changedCount === 0 && aiAvailable && fields.length > 0 && (
                            <Alert message="No PII changes suggested by AI. Your current PII settings appear to be correct." type="success" showIcon className="mx-4 mb-4" />
                        )}
                        <Table
                            dataSource={fields}
                            columns={this.getColumns()}
                            rowKey="fieldId"
                            pagination={false}
                            size="small"
                            scroll={{ x: 1100 }}
                            rowClassName={(record) => (this.hasChanges(record) ? "bg-blue-50" : "")}
                        />
                    </>
                )}
            </Modal>
        );
    }
}

PIISuggestionsModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
    savingPii: PropTypes.bool,
    piiSuggestionsData: PropTypes.object,
    onCancel: PropTypes.func.isRequired,
    onRegenerate: PropTypes.func,
    onSave: PropTypes.func.isRequired,
};

export default PIISuggestionsModal;
